/* FileDetailsViewComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./FileDetailsViewRenderer");

// const { VENDOR_LIST_FILTER, IMAGE_TYPES_FILTER } = require("../../../../lmrbcomponents/fileexplorer/fileConstants");

class FileDetailsViewComponent extends EventEmitter {
    constructor(logger, gameEditor, userSession, MUF) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._userSession = userSession;
        this._modalUserFeedback = MUF;

        this._osImage = undefined;
        this._activeOrgId = userSession.activeOrganization.id;
        this._renderer = new Renderer(this);

        this._imageTypeFilter = {};
        this._vendorListFilter = {};

        this._registerListeners();
    }

    get osImage() {
        return this._osImage;
    }

    get isDirty() {
        return this._osImage ? this._osImage.isDirty() : false;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    _registerFileListeners() {
        this._osImage.on(
            "DirtyStateChanged",
            (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this))
        );
        this._osImage.on(
            "PendingPatchesChanged",
            (this._onPendingPatchesChanged = this._onPendingPatchesChanged.bind(this))
        );
        this._osImage.on(
            "PristinePropertiesChanged",
            (this._onPristinePropertiesChanged = this._onPristinePropertiesChanged.bind(this))
        );
        this._osImage.on(
            "ImagePathsPristinePropertiesChanged",
            (this._onImagePathsPristinePropertiesChanged =
                this._onImagePathsPristinePropertiesChanged.bind(this))
        );
    }

    _unregisterFileListeners() {
        this._osImage.removeListener("DirtyStateChanged", this._onDirtyStateChanged);
        this._osImage.removeListener("PendingPatchesChanged", this._onPendingPatchesChanged);
        this._osImage.removeListener(
            "PristinePropertiesChanged",
            this._onPristinePropertiesChanged
        );
        this._osImage.removeListener(
            "ImagePathsPristinePropertiesChanged",
            this._onImagePathsPristinePropertiesChanged
        );
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }

    get gameEditor() {
        return this.getGameEditor();
    }
    getGameEditor() {
        return this._gameEditor;
    }

    shutdown() {
        if (!_.isUndefined(this._osImage)) {
            this._unregisterFileListeners();
        }
        this._unregisterListeners();
        return this._renderer.shutdown().then(
            () => {
                this._osImage = undefined;
                this._logger = undefined;
                this._gameEditor = undefined;
                this._renderer = undefined;
                this._imageTypeFilter = undefined;
                this._vendorListFilter = undefined;
            },
            (err) => {
                this._logger.warn(err, "FileDetailsViewComponent.shutdown() error.");
            }
        );
    }

    get logger() {
        return this.getLogger();
    }
    getLogger() {
        return this._logger;
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onDirtyStateChanged(dirtyState) {
        this.emit("Dirty", dirtyState);

        let onBeforeUnloadFunc = () => {
            return this._modalUserFeedback
                .displayConfirmation("Would you like to Save or Discard your changes?", {
                    title: "Unsaved Changes",
                    confirmDetails: {
                        text: "Save",
                        type: "additive",
                    },
                    rejectDetails: {
                        text: "Discard",
                        type: "text",
                    },
                })
                .then(() => {
                    if (this.isFileFormValid(true)) {
                        return this.saveFile(false).then(() => undefined); // it will not close the tab if it will have something in result
                    }
                })
                .catch((err) => {
                    if (err.userRejected) {
                        return Promise.resolve(this._osImage.clearPendingPatches(true))
                            .then(() => this._modalUserFeedback.hideDisplayed())
                            .then(() => this._gameEditor.getTabCmpnt().setDirty(false))
                            .then(() => undefined);
                    } else {
                        console.error("Error in onBeforeUnloadFunc! Err:", err);
                    }
                });
        };

        if (this.isDirty) {
            this._gameEditor.setOnBeforeUnload(onBeforeUnloadFunc);
        } else {
            this._gameEditor.clearOnBeforeUnload();
        }
    }

    _onPendingPatchesChanged() {
        // TODO - this is a quick hack because the Array ref adapter does a shutdown and complete rebuild every time you call setValue. the effect to the user is they lose focus in the proxy list if they stop typing longer than the debounce
        // this.emit('FilePropertiesChanged');
    }

    _onPristinePropertiesChanged(pristineProperties, before) {
        this.emit("FilePropertiesChanged", pristineProperties, before);
    }

    _onImagePathsPristinePropertiesChanged() {
        this.emit("FilePropertiesChanged");
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    get osImageJType() {
        return this._gameEditor.getOsImageJType();
    }

    get osImagePristineValues() {
        return this._gameEditor.getOsImagePristineValues(this._osImage);
    }

    whenModelIsReady() {
        return this._gameEditor
            .whenEditorIsReady()
            .then((osImage) => {
                this._osImage = osImage;

                this._imageTypeFilter = _.get(this._gameEditor, "_imageTypeFilter", {});
                this._vendorListFilter = _.get(this._gameEditor, "_vendorListFilter", {});

                const {vendor, imageType} = this.osImagePristineValues;

                if (!this._vendorListFilter[vendor] && vendor !== null) {
                    osImage.setVendor(null);
                }
                if (!this._imageTypeFilter[imageType] && imageType !== null) {
                    osImage.setImageType(null);
                }

                if (this._osImage.pendingPatches) {
                    return this._osImage.update({skipUpdatedOn: true, skipAudit: true});
                }
            })
            .catch((err) => {
                if (err && err.errorCode === "NotAuthorized") {
                    this._logger.warn(
                        err,
                        "FileDetailsViewComponent.whenModelIsReady() -> updateNoAudit() NotAuthorized"
                    );
                } else {
                    return Promise.reject(err);
                }
            })
            .then(() => {
                this._registerFileListeners();
            })
            .catch((err) => {
                this._logger.warn(err, "FileDetailsViewComponent.whenModelIsReady() error");
                this._modalUserFeedback.displayError({
                    errorMsg: "This file cannot be opened.",
                    errorCode: "",
                });
                return Promise.reject(err);
            });
    }

    resetFileProperties() {
        this._osImage.clearPendingPatches();
        return Promise.resolve();
    }

    isFileFormValid(rollbackInvalidData = false) {
        const file = this._osImage;
        const inputChanges = file.getProperties();
        const oldData = file.getPristineProperties();
        let inputName = inputChanges.name;
        let isInputNameEmpty;
        if (inputName[36] === "/") {
            inputName = inputName.substring(37);
        }
        isInputNameEmpty = inputName.trim().length === 0;

        if (isInputNameEmpty && rollbackInvalidData === false) {
            throw {
                errorMsg: "Name cannot be left empty. Please enter a valid file name.",
                errorCode: "SyntaxError",
                title: "Error",
            };
        } else if (isInputNameEmpty && rollbackInvalidData === true) {
            // Removing UUID prefix
            let name = _.get(oldData, "name", "");
            if (name[36] === "/") {
                name = name.substring(37);
            }
            file.setName(name.trim());
            return true;
        } else {
            file.setName(inputName.trim());
            return true;
        }
    }

    saveFile() {
        return Promise.resolve()
            .then(() => {
                //do nothing unless this guy is dirty
                if (this._osImage.isDirty()) {
                    this.isFileFormValid();
                    return this._osImage.update();
                }
            })
            .catch((err) => {
                this._logger.warn({error: err}, "FileDetailsViewComponent.saveFile() error.");
                err.errorMsg =
                    err.errorCode === "EntityAlreadyExists"
                        ? `The file name you entered already exists in the current folder.`
                        : err.errorMsg;
                if (err.errorCode === "NoPendingUpdates") {
                    this.emit("FilePropertiesChanged");
                    return Promise.reject();
                } else {
                    return this._modalUserFeedback.displayError(err);
                }
            });
    }
    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = FileDetailsViewComponent;

FileDetailsViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.userSession",
    "$teve.ui.userFeedback",
];

FileDetailsViewComponent.$displayDetails = {
    type: "View",
    name: "File Details",
    icon: "fileserver.edit_file",
    shown: true,
    title: "Details",
    order: 1,
};
